import {StageProfile} from './projectStageUtil';

export type CachedStage = {
	__typename: 'DefaultProjectStage' | 'CustomProjectStage';
	id: string;
	name: string;
	sortOrder: number;
	profile: StageProfile | null;
};

const SESSION_STORAGE_KEY = 'projectStages';

let cache: CachedStage[] = [];

// Session storage can be missing or throw (blocked site data, private windows); the cache then lives in memory only.
const withSessionStorage = <T>(action: (storage: Storage) => T): T | undefined => {
	try {
		return typeof window !== 'undefined' && window.sessionStorage ? action(window.sessionStorage) : undefined;
	} catch {
		return undefined;
	}
};

export const setProjectStages = (stages: CachedStage[]): void => {
	if (!Array.isArray(stages)) {
		return;
	}
	cache = [...stages];
	withSessionStorage(storage => storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(cache)));
};

export const getProjectStages = (): CachedStage[] => {
	if (cache.length === 0) {
		const stored = withSessionStorage(storage => storage.getItem(SESSION_STORAGE_KEY));
		if (stored) {
			try {
				const parsed = JSON.parse(stored);
				if (Array.isArray(parsed)) {
					cache = parsed;
				}
			} catch {
				cache = [];
			}
		}
	}
	return cache;
};

/** The first stage (by sortOrder) carrying the given profile. */
export const stageWithProfile = (profile: StageProfile): CachedStage | null =>
	[...getProjectStages()].sort((a, b) => a.sortOrder - b.sortOrder).find(stage => stage.profile === profile) ?? null;

export const clearProjectStages = (): void => {
	cache = [];
	withSessionStorage(storage => storage.removeItem(SESSION_STORAGE_KEY));
};
